import { graphql } from 'gql.tada'
import { useCallback, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import { useMutation } from 'urql'

import CheckIcon from '@/assets/icons/ic_logic-g-app_checkbox.svg'
import { EEditMode } from '@/components/toggle-edit'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import {
  WebPanel,
  WebPanelItem,
  WebPanelItemAction,
  WebPanelItemContent,
  WebPanelItemLabel,
} from '@/components/web-panel'
import { useQuery } from '@/hooks/useQuery'
import { maybeGraphQLType } from '@/utils/graphql'

type EmailNotification = 'email-noti-on' | 'email-noti-off'

type FormData = {
  emailNotification: EmailNotification
}

export const NotificationSection = (): JSX.Element => {
  const convertEmailNotiToString = useCallback((state: boolean | undefined): EmailNotification => {
    return (
      new Map<boolean, EmailNotification>([
        [true, 'email-noti-on'],
        [false, 'email-noti-off'],
      ]).get(state ?? false) ?? 'email-noti-off'
    )
  }, [])

  const [{ data: viewerData }] = useQuery({
    query: NotificationSection_Viewer,
  })

  const viewer = maybeGraphQLType('User')(viewerData?.currentUser)

  const [, updateEmailNotification] = useMutation(UpdateNotificationPreferenceMutation)

  const { handleSubmit, setValue } = useForm<FormData>({
    values: {
      emailNotification: convertEmailNotiToString(viewer?.emailNotification),
    },
  })

  const [editMode, setEditMode] = useState(EEditMode.View)

  const onUpdateEmailNotification = handleSubmit(async (formData) => {
    const result = await updateEmailNotification({
      input: {
        emailNotification: formData.emailNotification === 'email-noti-on',
      },
    })

    if (result.error || result.data?.notificationPreferenceUpdate?.errors?.length) {
      return toast.error('通知設定の変更に失敗しました', {
        toastId: 'profile-name-update-email-notification-failed',
        position: 'bottom-right',
      })
    }

    setEditMode(EEditMode.View)

    toast.success('メール受信設定を変更しました', {
      toastId: 'update-email-noti-success',
      position: 'bottom-right',
      icon: <CheckIcon width={24} />,
    })
  })

  return (
    <section className="mt-10">
      <h3 className="mb-2.5 text-base font-bold md:mb-3.5 md:text-[17px] lg:mb-5 lg:text-xl">
        通知設定
      </h3>

      <WebPanel>
        {editMode === EEditMode.View ? (
          <WebPanelItem>
            <WebPanelItemLabel>お知らせメール</WebPanelItemLabel>

            <WebPanelItemContent>
              <p className="text-xs font-bold md:text-sm lg:text-[15px]">
                {viewer?.emailNotification ? '受信する' : '受信しない'}
              </p>
              <p className="text-[11px] leading-tight md:leading-normal lg:text-xs">
                ※ 重要なお知らせについては別途送信する場合がございます。あらかじめご了承ください。
              </p>
            </WebPanelItemContent>

            <WebPanelItemAction>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="ml-2 h-[38px] w-[52px] rounded-lg text-xs md:h-[44px] md:w-[74px] md:rounded-xl md:text-[15px] lg:w-[80px]"
                onClick={() => setEditMode(EEditMode.Edit)}
              >
                変更
              </Button>
            </WebPanelItemAction>
          </WebPanelItem>
        ) : (
          <>
            <form onSubmit={onUpdateEmailNotification}>
              <WebPanelItem>
                <WebPanelItemLabel>お知らせメール</WebPanelItemLabel>

                <WebPanelItemContent className="flex w-full flex-wrap items-center pb-2.5 pt-5 md:flex-nowrap md:pb-0 md:pt-0">
                  <RadioGroup
                    defaultValue={convertEmailNotiToString(viewer?.emailNotification)}
                    className="flex w-full items-center justify-end gap-[13px] md:w-auto md:justify-start md:gap-[23px]"
                    onValueChange={(value: EmailNotification) => {
                      setValue('emailNotification', value)
                    }}
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem
                        value="email-noti-on"
                        id="email-noti-on"
                        aria-labelledby="email-noti-on-label"
                      />
                      <Label
                        id="email-noti-on-label"
                        htmlFor="email-noti-on"
                        className="leading-none text-primary-text"
                      >
                        受信する
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem
                        value="email-noti-off"
                        id="email-noti-off"
                        aria-labelledby="email-noti-off-label"
                      />
                      <Label
                        id="email-noti-off-label"
                        htmlFor="email-noti-off"
                        className="leading-none text-primary-text"
                      >
                        受信しない
                      </Label>
                    </div>
                  </RadioGroup>

                  <WebPanelItemAction
                    role="group"
                    className="mt-[15px] flex justify-end gap-2.5 md:mt-0 md:justify-normal"
                  >
                    <Button
                      variant="secondary"
                      size="sm"
                      type="submit"
                      className="h-[38px] w-[60px] rounded-lg text-xs md:h-[44px] md:w-[74px] md:rounded-xl md:text-[15px]"
                    >
                      変更
                    </Button>

                    <Button
                      variant="light"
                      className="h-[38px] w-[60px] rounded-lg text-xs md:h-[44px] md:w-[74px] md:rounded-xl md:text-[15px]"
                      size="sm"
                      type="button"
                      onClick={() => setEditMode(EEditMode.View)}
                    >
                      やめる
                    </Button>
                  </WebPanelItemAction>
                </WebPanelItemContent>
              </WebPanelItem>
            </form>
          </>
        )}
      </WebPanel>
    </section>
  )
}

const NotificationSection_Viewer = graphql(`
  query NotificationSection_Viewer {
    currentUser {
      __typename
      ... on User {
        id
        emailNotification
      }
    }
  }
`)

const UpdateNotificationPreferenceMutation = graphql(`
  mutation UpdateNotificationPreferenceMutation($input: NotificationPreferenceUpdateInput!) {
    notificationPreferenceUpdate(input: $input) {
      viewer {
        id
        emailNotification
      }

      errors {
        __typename
      }
    }
  }
`)
